import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { REACTIONS } from "@/lib/guest";
import {
  Trophy,
  ArrowLeft,
  Sparkles,
  Camera,
  Calendar,
  Share2,
  Award,
} from "lucide-react";

const PODIUM = [
  { rank: 1, size: "lg", color: "#FFD700", label: "1º" },
  { rank: 2, size: "md", color: "#C0C0C0", label: "2º" },
  { rank: 3, size: "md", color: "#CD7F32", label: "3º" },
];

export default function HallOfFamePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api
      .get(`/public/events/${slug}/hall-of-fame?limit=12`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const int = setInterval(load, 10000);
    return () => clearInterval(int);
    // eslint-disable-next-line
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-black text-white flex items-center justify-center">
        <div className="text-white/50">Carregando hall da fama...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-[100dvh] bg-black text-white flex items-center justify-center p-6">
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <h1 className="font-display font-bold text-xl tracking-tight">Ops!</h1>
          <p className="text-white/60 mt-2">Evento não encontrado.</p>
        </div>
      </div>
    );
  }

  const top3 = data.top.slice(0, 3);
  const rest = data.top.slice(3);
  const winner = top3[0];

  return (
    <div className="min-h-[100dvh] text-white grain relative overflow-hidden">
      {/* Backdrop glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#FF007F]/20 blur-3xl" />
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full bg-[#00F0FF]/15 blur-3xl" />
      </div>

      <div className="relative sticky top-0 z-30 glass px-4 py-3 flex items-center justify-between">
        <Link
          to={`/e/${slug}/gallery`}
          className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
          data-testid="hall-back"
        >
          <ArrowLeft className="w-4 h-4" /> Galeria
        </Link>
        <div className="text-xs text-white/50 uppercase tracking-[0.2em]">
          {data.event.name}
        </div>
        <Link
          to={`/e/${slug}/slideshow`}
          className="text-xs text-white/70 hover:text-[#FF007F] transition-colors"
          data-testid="hall-slideshow"
        >
          Telão →
        </Link>
      </div>

      <div className="relative max-w-5xl mx-auto px-6 py-10">
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="inline-flex items-center gap-3 mb-4"
          >
            <Trophy className="w-8 h-8 text-[#FFD700]" />
            <span className="text-xs uppercase tracking-[0.3em] text-white/60">
              Hall da Fama
            </span>
            <Trophy className="w-8 h-8 text-[#FFD700]" />
          </motion.div>
          <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tighter">
            As <span className="text-[#FF007F] text-glow-pink">mais amadas</span> do evento
          </h1>
          <p className="text-white/60 mt-3">
            Ranking por reações dos convidados · atualiza ao vivo
          </p>
        </div>

        {top3.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center max-w-md mx-auto">
            <Award className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <h3 className="font-display font-bold text-xl tracking-tight">
              Sem reações ainda
            </h3>
            <p className="text-white/50 mt-2">
              Volte quando alguém curtir as fotos!
            </p>
            <Link to={`/e/${slug}/gallery`} className="btn-neon mt-6 inline-flex">
              <Camera className="w-4 h-4" /> Ver galeria
            </Link>
          </div>
        ) : (
          <>
            {/* Winner spotlight */}
            {winner && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="mb-10"
              >
                <div className="relative max-w-md mx-auto">
                  <div className="absolute -inset-6 bg-gradient-to-br from-[#FFD700]/40 via-[#FF007F]/30 to-[#FFD700]/40 blur-2xl" />
                  <div className="relative glass rounded-3xl p-6 border-2 border-[#FFD700]/60">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <Trophy className="w-6 h-6 text-[#FFD700]" />
                      <span className="font-display font-black text-2xl tracking-tighter text-[#FFD700]">
                        CAMPEÃ
                      </span>
                      <Trophy className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <div className="aspect-square rounded-2xl overflow-hidden mb-4">
                      <img
                        src={fileUrl(winner.storage_path)}
                        alt=""
                        className="w-full h-full object-cover"
                        data-testid={`winner-photo`}
                      />
                    </div>
                    <div className="text-center">
                      <div className="text-xs uppercase tracking-[0.2em] text-white/50">
                        Foto de
                      </div>
                      <div className="font-display font-bold text-xl tracking-tight text-[#FF007F] mt-1">
                        {winner.guest_name}
                      </div>
                      <div className="flex items-center justify-center gap-2 mt-3 text-2xl">
                        {REACTIONS.map((r) =>
                          winner.reactions?.[r.key] ? (
                            <span key={r.key} className="flex items-center gap-0.5">
                              <span>{r.char}</span>
                              <span className="text-sm text-white/70 font-bold">
                                {winner.reactions[r.key]}
                              </span>
                            </span>
                          ) : null,
                        )}
                      </div>
                      <div className="mt-3 text-sm text-white/60">
                        <span className="text-[#FFD700] font-black text-2xl">
                          {winner.reactions_total}
                        </span>{" "}
                        reações no total
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* 2nd + 3rd */}
            {top3.length > 1 && (
              <div className="grid sm:grid-cols-2 gap-6 mb-10 max-w-2xl mx-auto">
                {top3.slice(1).map((p, i) => {
                  const rank = i + 2;
                  const medalColor = rank === 2 ? "#C0C0C0" : "#CD7F32";
                  return (
                    <motion.div
                      key={p.id}
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1 * rank }}
                      className="glass rounded-2xl p-4 relative overflow-hidden"
                      data-testid={`podium-${rank}`}
                    >
                      <div className="flex gap-4">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center font-display font-black text-sm"
                          style={{
                            background: medalColor,
                            color: "#000",
                          }}
                        >
                          {rank}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="aspect-square rounded-xl overflow-hidden mb-3">
                            <img
                              src={fileUrl(p.storage_path)}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="text-sm font-bold truncate">{p.guest_name}</div>
                          <div className="text-xs text-white/60 mt-1 flex items-center gap-2">
                            <span
                              className="font-black"
                              style={{ color: medalColor }}
                            >
                              {p.reactions_total}
                            </span>
                            <span>reações</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Rest of top 12 */}
            {rest.length > 0 && (
              <>
                <div className="text-center mb-4">
                  <span className="text-xs uppercase tracking-[0.2em] text-white/40">
                    Menções honrosas
                  </span>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                  {rest.map((p, i) => (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.05 * i }}
                      className="glass rounded-xl overflow-hidden"
                      data-testid={`honor-${p.id}`}
                    >
                      <div className="aspect-square relative">
                        <img
                          src={fileUrl(p.storage_path)}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-1.5 left-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-black/70 text-white">
                          #{i + 4}
                        </div>
                        <div className="absolute bottom-1.5 right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#FF007F] text-white">
                          {p.reactions_total} ❤
                        </div>
                      </div>
                      <div className="px-2 py-1 text-[11px] text-white/70 truncate text-center">
                        {p.guest_name}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        <div className="mt-12 text-center">
          <Link to={`/e/${slug}/gallery`} className="btn-cyan" data-testid="hall-back-to-gallery">
            <Camera className="w-4 h-4" /> Voltar para galeria
          </Link>
        </div>
      </div>
    </div>
  );
}
